import { useState } from 'react'

import {
  sendTestPayloadWebhook,
  type PayloadWebhookTestSendResult,
} from '../../api/webhooks.ts'
import { WEBHOOK_TYPES } from '../../routes/webhooks/types.ts'
import { CodeBlock } from '../CodeBlock.tsx'

import {
  EXAMPLE_EVENTS,
  exampleEnvelopeJsonFor,
  exampleHeadersFor,
  examplePayloadFor,
  momentFor,
  type ExampleEvent,
} from './payload-webhook-example-events.ts'
import {
  ExplorerLayout,
  ExplorerOption,
  ExplorerSendSection,
  SectionLabel,
} from './webhook-explorer.tsx'

// The payload-events explorer on /docs/webhooks/payloads/events and the setup
// page, mounted from markdown via `::payload-webhook-events`
// (src/docs/directives.tsx).
//
// The delivery lifecycle explorer's counterpart, and the same bargain: left,
// the events a payload emits; right, the exact envelope Curri would POST — and,
// signed in, a button that really sends it at the reader's configured sandbox
// endpoint (POST /api/webhooks/payloads/test) and reports what came back. Curri
// signs that send with the endpoint's own secret, so it is also the one place a
// reader can prove their verification code works before real freight depends on
// it — which is what the signature note under the outcome is for.
//
// The shell around all of that is shared with the delivery explorer; see
// webhook-explorer.tsx.

const SETUP_PATH = WEBHOOK_TYPES.payloads.docs.setup
const SANDBOX_VARIANT = WEBHOOK_TYPES.payloads.variants.sandbox

const headerLinesFor = (event: ExampleEvent): string =>
  Object.entries(exampleHeadersFor(event))
    .map(([name, value]) => `${name}: ${value}`)
    .join('\n')

export const PayloadWebhookEventsExplorer = ({
  onNavigate,
}: {
  onNavigate?: (path: string) => void
}) => {
  const [event, setEvent] = useState<ExampleEvent>('payload.created')

  return (
    <ExplorerLayout
      nav={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {EXAMPLE_EVENTS.map(candidate => (
            <ExplorerOption
              active={candidate === event}
              description={momentFor(candidate).description}
              key={candidate}
              label={candidate}
              onSelect={() => setEvent(candidate)}
            />
          ))}
        </div>
      }
      navLabel="Payload events"
    >
      <p style={{ margin: 0, fontSize: 14, color: 'var(--curri-text-secondary)' }}>
        {momentFor(event).caption}
      </p>
      <CodeBlock code={exampleEnvelopeJsonFor(event)} language="json" />
      <div>
        <SectionLabel>Headers</SectionLabel>
        <CodeBlock code={headerLinesFor(event)} language="http" />
      </div>
      <div style={{ borderTop: '1px solid var(--curri-border)', paddingTop: 12 }}>
        <ExplorerSendSection
          copy={{
            item: 'event',
            operation: 'test event',
            endpoint: 'sandbox payload endpoint',
          }}
          // Only this explorer says so, because only these sends are signed.
          footerFor={(sent: PayloadWebhookTestSendResult) =>
            sent.headers['Curri-Signature'] !== undefined && (
              <p style={{ margin: '6px 0 0', fontSize: 12.5, color: 'var(--curri-text-tertiary)' }}>
                Signed with your endpoint&apos;s secret — the same signature a
                real event carries.
              </p>
            )
          }
          onNavigate={onNavigate}
          sandboxVariant={SANDBOX_VARIANT}
          selection={event}
          send={apiFetch =>
            sendTestPayloadWebhook(apiFetch, {
              type: event,
              payload: examplePayloadFor(event),
            })
          }
          setupPath={SETUP_PATH}
        />
      </div>
    </ExplorerLayout>
  )
}
